import React, { useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import Tab from './Tab'
import Img from 'gatsby-image'

const StyledWrapper = styled.div`
  left: 50%;
  margin-left: -420px;
  overflow: hidden;
  position: relative;
  width: 840px;

  ${({ theme }) => theme.media.desktop} {
    left: 0;
    margin: 0 auto;
    width: 100%;
  }
`

const StyledCircle = styled.div`
  border-radius: 50%;
  height: 502px;
  margin: 0 auto;
  object-fit: contain;
  opacity: 0.6;
  width: 502px;

  ${({ theme }) => theme.media.tablet} {
    height: 582px;
    width: 582px;
  }
`

const StyledIconList = styled.ul`
  display: flex;
  left: 50%;
  margin-left: calc(-50vw + 20px);
  position: absolute;
  top: 0;

  > * {
    transform: translate3d(
      ${({ activeItem }) => `-${activeItem * 100}%`},
      0,
      0
    );
  }

  ${({ theme }) => theme.media.tablet} {
    margin-left: -291px;
  }
`

const StyledIcon = styled.li`
  display: flex;
  justify-content: center;
  opacity: ${({ isActive }) => (isActive ? '1' : '0')};
  position: relative;
  transition: opacity 0.4s ease, transform 0.4s ease;
  width: calc(100vw - 40px);

  div {
    width: 100%;
  }

  ${({ theme }) => theme.media.tablet} {
    width: 582px;
  }

  ${({ breakingConvention }) =>
    breakingConvention &&
    css`
      margin-top: 80px !important;

      img {
        height: 70% !important;
      }

      ${({ theme }) => theme.media.tablet} {
        margin-top: 120px !important;
      }
    `}
`

const StyledTabList = styled.ul`
  border-bottom: 1px solid rgba(78, 93, 120, 0.12);
  bottom: 85px;
  display: flex;
  flex-wrap: nowrap;
  left: 0;
  list-style-type: none;
  padding: 0;
  position: absolute;
  right: 0;
  transition: transform 0.4s ease;
  transform: translate3d(
    ${({ activeItem }) =>
      activeItem === 0 ? '33%' : `-${activeItem * 33.33 - 33.33}%`},
    0,
    0
  );

  ${({ theme }) => theme.media.desktop} {
    transform: translate3d(0, 0, 0);
  }
`

const StyledTabMark = styled.div`
  background: ${({ theme }) => theme.colors.red};
  bottom: 86px;
  height: 2px;
  opacity: 0.33;
  position: absolute;
  transform: translate3d(100%, 0, 0);
  transition: transform 0.4s ease;
  width: 33.333333%;

  ${({ theme }) => theme.media.desktop} {
    transform: translate3d(${({ activeItem }) => `${activeItem * 100}%`}, 0, 0);
  }
`

const StyledIMGWrapper = styled.div`
  display: flex;
  justify-content: center;
  max-height: 352px;
  max-width: calc(100vw - 40px);

  img {
    height: 100%;
    width: auto;
  }
`

const ToolIcon = props => {
  if (props.icon.hasOwnProperty('sys')) {
    return (
      <StyledIcon isActive={props.isActive}>
        <StyledIMGWrapper>
          <img alt="icon" loading="lazy" src={props.icon.fields.file.url} />
        </StyledIMGWrapper>
      </StyledIcon>
    )
  } else if (!props.icon.fluid.src) {
    return (
      <StyledIcon
        breakingConvention={true}
        style={{ marginTop: '120px' }}
        isActive={props.isActive}
      >
        <StyledIMGWrapper>
          <img alt="icon" loading="lazy" src={props.icon.file.url} />
        </StyledIMGWrapper>
      </StyledIcon>
    )
  } else {
    return (
      <StyledIcon isActive={props.isActive}>
        <Img fluid={props.icon.fluid} />
      </StyledIcon>
    )
  }
}

const TabList = ({ tabs, cmsData }) => {
  const standarizedData = []
  cmsData &&
    cmsData.items.map((item, index) => {
      if (item.fields) {
        item.fields.id = index
        standarizedData.push(item.fields)
      }
      return null
    })

  const data = cmsData ? standarizedData : tabs

  const [activeSlide, setActiveSlide] = useState(1)
  const handlers = useSwipeable({
    onSwiped: event => {
      event.dir === 'Right' &&
        activeSlide !== 0 &&
        setActiveSlide(activeSlide - 1)
      event.dir === 'Left' &&
        activeSlide !== data.length - 1 &&
        setActiveSlide(activeSlide + 1)
    },
    config: {
      delta: 10,
      preventDefaultTouchmoveEvent: false,
      trackTouch: true,
      trackMouse: true,
      rotationAngle: 0,
    },
  })

  return (
    <StyledWrapper {...handlers}>
      <StyledCircle />
      <StyledIconList activeItem={activeSlide}>
        {data.map(tab => {
          return (
            <ToolIcon
              key={tab.id}
              icon={tab.icon}
              isActive={tab.id === data[activeSlide].id}
            />
          )
        })}
      </StyledIconList>
      <StyledTabList activeItem={activeSlide}>
        {data.map((tab, index) => {
          return (
            <Tab
              key={tab.id}
              data={tab}
              isActive={tab.id === data[activeSlide].id}
              onClick={() => {
                setActiveSlide(index)
              }}
            />
          )
        })}
      </StyledTabList>
      <StyledTabMark activeItem={activeSlide} />
    </StyledWrapper>
  )
}

TabList.propTypes = {
  tabs: PropTypes.array.isRequired,
}

export default TabList
